import type {
  Condition,
  ConditionAnd,
  ConditionLeaf,
  ConditionNot,
  ConditionOr,
  ConditionTrigger,
  ExprConditionLeaf
} from '@axilon-apps/domains-ontology-ui-state'
import { MAX_EXPR_SOURCES } from '@axilon-apps/domains-ontology-ui-state'

type LeafKind = ConditionLeaf['kind']

export function defaultLeaf(kind: LeafKind): ConditionLeaf {
  if (kind === 'live') {
    return { kind: 'live', tag_id: '', cmp: 'gt', value: 0 }
  }
  if (kind === 'agg') {
    return { kind: 'agg', tag_id: '', fn: 'avg', window: '5m', cmp: 'gt', value: 0 }
  }
  if (kind === 'alarm_active') {
    return { kind: 'alarm_active', tag_id: '', alarm_name: '' }
  }
  if (kind === 'expr') {
    return defaultExprLeaf()
  }
  return {
    kind: 'deviation',
    tag_id: '',
    window: '1h',
    cmp: 'gt',
    value: 0,
    baseline_fn: 'avg',
    metric: 'zscore'
  }
}

export function defaultExprLeaf(): ExprConditionLeaf {
  return {
    kind: 'expr',
    expression: 'a + b',
    sources: {
      a: { tag_id: '' },
      b: { tag_id: '' }
    },
    cmp: 'gte',
    value: 0
  }
}

export function nextExprAlias(existing: readonly string[]): string {
  const taken = new Set(existing)
  const letters = 'abcdefghijklmnopqrstuvwxyz'
  for (const ch of letters) {
    if (!taken.has(ch)) return ch
  }
  for (let i = 2; i < 100; i++) {
    for (const ch of letters) {
      const alias = `${ch}${i}`
      if (!taken.has(alias)) return alias
    }
  }
  return `x${existing.length}`
}

export function canAddExprSource(sources: Record<string, unknown>): boolean {
  return Object.keys(sources).length < MAX_EXPR_SOURCES
}

export function defaultAnd(): ConditionAnd {
  return { kind: 'and', operands: [] }
}

export function defaultOr(): ConditionOr {
  return { kind: 'or', operands: [] }
}

export function defaultNot(): ConditionNot {
  return { kind: 'not', operand: defaultLeaf('live') }
}

export function defaultConditionRoot(): Condition {
  return defaultAnd()
}

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `tr-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export function newRcaTrigger(partial?: Partial<ConditionTrigger>): ConditionTrigger {
  return {
    id: newId(),
    name: 'New trigger',
    purpose: '',
    condition: defaultConditionRoot(),
    ...partial
  }
}

/** Normalize legacy `description` into `purpose` when purpose is empty. */
export function normalizeTriggerFields(t: ConditionTrigger): ConditionTrigger {
  const purpose = t.purpose?.trim() || t.description?.trim() || ''
  return {
    ...t,
    id: t.id?.trim() || newId(),
    name: t.name?.trim() || '',
    purpose,
    condition: t.condition
  }
}
